import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Link } from 'react-router-dom'
import { LockIcon } from '@/components/readiness/LockedItem'
import { trackReadiness } from '@/lib/readiness/profile'
import { DEFAULT_INDEX_ID } from '@/lib/indexes/registry'
import { useIsLocked, type GateId } from '@/stores/profileStore'

// ── Locked preview (Iteration 6 POC §5.3, Note 4) ───────────────────────────
// A gated page renders as built: everything above `gateAt` (the first gated
// block) stays live; from the anchor down the real content still renders but
// is inert (no pointer, no focus, hidden from assistive tech) under a fade-out
// mask, with the unlock card on it. The region is clamped to the card's bottom
// plus 80px, so nothing below the card is reachable by scroll. With the
// feature available it renders its children untouched.

const CLAMP_PAD = 80

interface LockedPreviewProps {
  feature: GateId
  gateAt: RefObject<HTMLElement | null>
  title: string
  body: string
  // Card top as a share of the gated region (34% on Progress, whose region is taller).
  cardAt?: number
  children: ReactNode
}

export function LockedPreview(p: LockedPreviewProps) {
  const locked = useIsLocked(p.feature)
  if (!locked) return <>{p.children}</>
  return <Gate {...p} />
}

// The anchor and everything after it, at every level up to the content root.
function gatedElements(anchor: HTMLElement, root: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = []
  let el: HTMLElement | null = anchor
  out.push(anchor)
  while (el && el !== root) {
    let sib = el.nextElementSibling as HTMLElement | null
    while (sib) {
      out.push(sib)
      sib = sib.nextElementSibling as HTMLElement | null
    }
    el = el.parentElement
  }
  return out
}

function Gate({ feature, gateAt, title, body, cardAt = 0.42, children }: LockedPreviewProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ anchorTop: number; contentH: number; cardH: number } | null>(null)

  // Measure the anchor, the content and the card; re-measure on any resize.
  useLayoutEffect(() => {
    const content = contentRef.current
    const card = cardRef.current
    if (!content || !card) return
    const measure = () => {
      const anchor = gateAt.current
      if (!anchor) return
      const anchorTop = anchor.getBoundingClientRect().top - content.getBoundingClientRect().top
      const next = { anchorTop: Math.round(anchorTop), contentH: Math.round(content.scrollHeight), cardH: Math.round(card.offsetHeight) }
      setBox((b) => (b && b.anchorTop === next.anchorTop && b.contentH === next.contentH && b.cardH === next.cardH ? b : next))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(content)
    ro.observe(card)
    if (gateAt.current) ro.observe(gateAt.current)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [gateAt])

  // Inert from the anchor down. Re-applied after every render so blocks that
  // mount later (data arriving) are covered too.
  useLayoutEffect(() => {
    const root = contentRef.current
    const anchor = gateAt.current
    if (!root || !anchor) return
    const els = gatedElements(anchor, root)
    els.forEach((el) => {
      el.setAttribute('inert', '')
      el.setAttribute('aria-hidden', 'true')
      el.style.pointerEvents = 'none'
    })
    return () =>
      els.forEach((el) => {
        el.removeAttribute('inert')
        el.removeAttribute('aria-hidden')
        el.style.pointerEvents = ''
      })
  })

  useEffect(() => {
    trackReadiness({ name: 'readiness.locked_preview_viewed', feature })
  }, [feature])

  const gatedH = box ? Math.max(0, box.contentH - box.anchorTop) : 0
  const cardTop = box ? box.anchorTop + Math.round(gatedH * cardAt) : 0
  const height = box ? cardTop + box.cardH + CLAMP_PAD : undefined

  return (
    <div className="rd-locked-preview relative" style={{ height, overflowY: 'clip' }} data-testid={`locked-preview-${feature}`}>
      <div ref={contentRef}>{children}</div>
      {box && (
        <div
          className="rd-locked-mask pointer-events-none absolute"
          style={{ top: box.anchorTop, bottom: 0, left: -16, right: -16 }}
          aria-hidden="true"
          data-testid="locked-mask"
        />
      )}
      <div
        ref={cardRef}
        role="region"
        aria-label={`${title} · locked`}
        className="rd-unlock-card absolute left-1/2 -translate-x-1/2"
        style={{ top: cardTop, visibility: box ? 'visible' : 'hidden' }}
        data-testid="unlock-card"
      >
        <span className="rd-unlock-icon" aria-hidden="true"><LockIcon size={20} /></span>
        <h2 className="mt-4 text-[20px] font-bold leading-tight">{title}</h2>
        <p className="mx-auto mt-2 max-w-[460px] text-[14px] leading-[1.5]" style={{ color: 'var(--rd-text-muted)' }}>{body}</p>
        <Link
          to={`/readiness?index=${DEFAULT_INDEX_ID}`}
          className="rd-btn mt-5 !min-h-[44px] !px-5"
          onClick={() => trackReadiness({ name: 'readiness.locked_preview_dismissed', feature })}
          data-testid="unlock-back"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  )
}
